/**
 * MilitaryTranslationPreview — Dashboard State Architecture, Step 5.
 *
 * Renders in State 2 / State 3 only. A small, honest confidence-builder
 * that shows the user the platform has read their military background.
 *
 * STRICT RULES:
 *   - existing resume data only (no AI, no new API)
 *   - never invent translation if data is missing → render the fallback prompt
 *   - no military iconography (no flags, ranks, branches as graphics)
 *   - no "veteran-friendly" or hype copy
 *   - calm, quiet, secondary
 */

import { Card } from '@/components/ui/card';
import { sanitizeMosTitle } from '@/utils/militaryTranslation';

function uniqueChips(arrays, max = 4) {
  const seen = new Set();
  const out = [];
  for (const arr of arrays) {
    if (!Array.isArray(arr)) continue;
    for (const v of arr) {
      if (typeof v !== 'string') continue;
      const t = v.trim();
      if (!t || seen.has(t.toLowerCase())) continue;
      seen.add(t.toLowerCase());
      out.push(t);
      if (out.length >= max) return out;
    }
  }
  return out;
}

export function MilitaryTranslationPreview({ resume, compact = false, isResumeComplete = false }) {
  const roles = Array.isArray(resume?.military_roles) ? resume.military_roles : [];
  if (roles.length === 0) return null; // no role → component not rendered at all

  // Use the most recent role: prefer one with start_year, else first.
  const role =
    [...roles]
      .filter((r) => r && (r.start_year || r.end_year))
      .sort((a, b) => (b.end_year || 0) - (a.end_year || 0))[0] || roles[0];

  const headline =
    role?.civilian_title ||
    role?.role_family ||
    role?.translated_title ||
    sanitizeMosTitle(role?.mos_title, role?.rank) ||
    null;

  const branch = role?.branch || null;
  const rank = role?.rank || null;
  const mosCode = role?.mos_code || null;

  const metaParts = [branch, rank, mosCode].filter(Boolean);

  const chips = uniqueChips(
    [role?.selected_skills, role?.selected_knowledge, role?.selected_abilities],
    4
  );

  // A headline is enough for the honest path. We previously required
  // metaParts too, which forced completed users with a clean translation
  // (but minimal meta) into the fallback. That misread valid military
  // experience as "missing MOS" and read as confusing/erosive.
  const hasUsefulTranslation = Boolean(headline);

  // Fallback: military role exists but no clean translation data.
  if (!hasUsefulTranslation) {
    // Completed resumes already represent a deliberate, finished record.
    // Showing them an "Add or update your MOS" nudge implies their
    // military data is missing when in fact they finished the document.
    // Stay quiet in that case — never undermine a completed user.
    if (isResumeComplete) return null;
    return (
      <Card
        data-testid="military-translation-preview"
        data-variant={compact ? 'compact' : 'full'}
        className="border border-slate-200 shadow-none"
      >
        <div className={compact ? 'px-5 py-3' : 'px-5 py-4'}>
          <p className="text-[11px] font-semibold tracking-wider uppercase text-slate-400 mb-1">
            Your military experience translates to
          </p>
          <p className="text-sm text-slate-600" data-testid="military-translation-fallback">
            Add or update your MOS to improve civilian role translation.
          </p>
        </div>
      </Card>
    );
  }

  // Honest path: render what we actually have.
  return (
    <Card
      data-testid="military-translation-preview"
      data-variant={compact ? 'compact' : 'full'}
      className="border border-slate-200 shadow-none"
    >
      <div className={compact ? 'px-5 py-3' : 'px-5 py-4'}>
        <p className="text-[11px] font-semibold tracking-wider uppercase text-slate-400 mb-1">
          Your military experience translates to
        </p>
        <p
          className={`font-semibold text-slate-900 ${compact ? 'text-base' : 'text-lg'}`}
          data-testid="military-translation-headline"
        >
          {headline}
        </p>
        {metaParts.length > 0 && (
          <p className="text-xs text-slate-500 mt-1" data-testid="military-translation-meta">
            {metaParts.join(' \u00B7 ')}
          </p>
        )}
        {chips.length > 0 && !compact && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="military-translation-chips">
            {chips.map((c, i) => (
              <span
                key={`${c}-${i}`}
                className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-50 text-slate-700 border border-slate-200"
              >
                {c}
              </span>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export default MilitaryTranslationPreview;
